<template>
  <!-- Меню кнопки тулбара карты: «Инструменты» (линейка, метка, редактор) и
       «Вид» (3D, компас) - пункты одним меню, а не россыпью кнопок. -->
  <div ref="rootEl" class="tool-menu">
    <button
      ref="triggerEl"
      type="button"
      class="locate-btn"
      :class="{ 'locate-btn--on': open || anyActive }"
      aria-haspopup="menu"
      :aria-expanded="open"
      :aria-label="menuLabel"
      :title="menuLabel"
      @click="toggle"
    >
      <component :is="icon ?? Wrench" class="locate-btn__icon" aria-hidden="true" />
    </button>

    <div v-if="open" ref="menuEl" role="menu" class="tool-menu__panel paper-card" :aria-label="menuLabel" @keydown="onMenuKeydown">
      <button
        v-for="item in visibleItems"
        :key="item.key"
        type="button"
        role="menuitemcheckbox"
        :aria-checked="Boolean(item.active)"
        class="tool-menu__item"
        :class="item.active && 'tool-menu__item--on'"
        @click="pick(item.key)"
      >
        <component :is="item.icon" class="w-4 h-4 shrink-0" aria-hidden="true" />
        <span class="flex-1 text-left">{{ item.label }}</span>
        <Check v-if="item.active" class="w-4 h-4 shrink-0" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, type Component } from "vue";
import { useI18n } from "vue-i18n";
import { Check, Wrench } from "lucide-vue-next";

const props = defineProps<{
  items: { key: string; label: string; icon: Component; active?: boolean; hidden?: boolean }[];
  anyActive: boolean;
  label?: string;
  icon?: Component;
}>();
const { t } = useI18n();
const menuLabel = computed(() => props.label ?? t("map.tools.button"));
const emit = defineEmits<{ (e: "pick", key: string): void }>();

const open = ref(false);
const rootEl = ref<HTMLElement | null>(null);
const triggerEl = ref<HTMLButtonElement | null>(null);
const menuEl = ref<HTMLElement | null>(null);
const visibleItems = computed(() => props.items.filter((i) => !i.hidden));

const menuItems = () => Array.from(menuEl.value?.querySelectorAll<HTMLElement>('[role="menuitemcheckbox"]') ?? []);

const toggle = async () => {
  open.value = !open.value;
  if (open.value) {
    await nextTick();
    menuItems()[0]?.focus();
  }
};

const close = (returnFocus: boolean) => {
  open.value = false;
  if (returnFocus) triggerEl.value?.focus();
};

const pick = (key: string) => {
  emit("pick", key);
  close(false);
};

// Стрелки - по пунктам по кругу, Esc - закрыть и вернуть фокус на кнопку.
const onMenuKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape") {
    e.preventDefault();
    close(true);
    return;
  }
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
  e.preventDefault();
  const items = menuItems();
  const idx = items.indexOf(document.activeElement as HTMLElement);
  const next = e.key === "ArrowDown" ? (idx + 1) % items.length : (idx - 1 + items.length) % items.length;
  items[next]?.focus();
};

const onDocPointerDown = (e: PointerEvent) => {
  if (open.value && rootEl.value && !rootEl.value.contains(e.target as Node)) close(false);
};
onMounted(() => document.addEventListener("pointerdown", onDocPointerDown));
onBeforeUnmount(() => document.removeEventListener("pointerdown", onDocPointerDown));
</script>

<style scoped>
/* Как панель «Слоёв»: позиционируется от всего .map-controls (без
   position: relative здесь) - на десктопе слева от тулбара, на мобильном
   над горизонтальным тулбаром. */
.tool-menu__panel {
  position: absolute;
  bottom: 0;
  right: calc(100% + 8px);
  width: 200px;
  padding: 6px;
  z-index: 35;
}
.tool-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
  border-radius: calc(var(--radius) - 2px);
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
}
.tool-menu__item:hover {
  background: var(--paper-2);
}
.tool-menu__item--on {
  background: var(--forest-hex);
  color: #fff;
}
.tool-menu__item--on:hover {
  background: var(--forest-hex);
}
</style>
